import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Aviso } from '@/components/ui/aviso';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { EstadoError } from '@/components/ui/estado-vacio';
import { Etiqueta } from '@/components/ui/etiqueta';
import { GrupoDeOpciones } from '@/components/ui/grupo-de-opciones';
import { Input } from '@/components/ui/input';
import { SelectNativo } from '@/components/ui/select-nativo';
import { Skeleton } from '@/components/ui/skeleton';
import { Slider } from '@/components/ui/slider';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { useToast } from '@/lib/hooks/use-toast';
import { cn } from '@/lib/utils';
import {
  formatoCantidad,
  formatoDiferencia,
  formatoFechaYHora,
  formatoPorcentaje,
  formatoPrecio,
} from '@/lib/utils/formato-precio';
import {
  CAMBIO,
  FILTROS_DEL_CUADRO,
  MARGEN,
  ORDEN_INICIAL,
  RESULTADOS,
  cambiarOrden,
  cantidadOGuion,
  categoriaDe,
  competidorDe,
  contarChips,
  cortoDelDisparo,
  marcaDe,
  opcionesDe,
  ordenar,
  pasaElFiltro,
  porQueDelResultado,
  productosOGuion,
  quienLaLanzo,
  sinCambiosHoy,
  textoDelExtremo,
  tituloDelDia,
  diaDelEvento,
  totales,
  cuantasPropuso,
  type ChipDelCuadro,
  type ColumnaDelCuadro,
  type FiltrosDelCuadro,
  type OrdenDelCuadro,
} from '../../lib/corridas';
import { diaDeBolivia } from '../../lib/rango-de-fechas';
import { corridasApi } from '../../services/corridas-api';
import type { DestinoHoy, FiltroBandeja } from '../../types/price-proposals.types';
import type { EventoDeLaLinea, ProductosDelEvento } from '../../types/corridas.types';
import { ProductHistoryDialog } from '../product-history-dialog';
import { TextoRecortable } from '../texto-recortable';
import { EnlaceAProducto } from '@/components/ui/enlace-a-producto';

const TEXTO = 'min-w-0 [overflow-wrap:anywhere]';

/** Las columnas: título, cómo se ordena (null = no se ordena), alineación y ancho (suman 100 %). */
const COLUMNAS: Array<{
  titulo: string;
  columna: ColumnaDelCuadro | null;
  derecha: boolean;
  ancho: number;
}> = [
  { titulo: 'Producto', columna: 'nombre', derecha: false, ancho: 20 },
  { titulo: 'Antes → propuesto (Bs)', columna: 'propuesto', derecha: true, ancho: 13 },
  { titulo: 'Cambio', columna: 'cambioPct', derecha: true, ancho: 9 },
  { titulo: 'Margen', columna: 'margenPct', derecha: true, ancho: 7 },
  { titulo: 'Más barato (Bs)', columna: 'masBarato', derecha: true, ancho: 11 },
  { titulo: 'Qué pasó', columna: 'resultado', derecha: false, ancho: 18 },
  { titulo: 'Hoy (Bs)', columna: 'precioDeHoy', derecha: true, ancho: 10 },
  { titulo: 'Acciones', columna: null, derecha: true, ancho: 12 },
];

const CHIPS: Array<{ clave: ChipDelCuadro; texto: string; fondo?: string }> = [
  { clave: 'TODAS', texto: 'Todas' },
  ...RESULTADOS.map((r) => ({ clave: r.clave as ChipDelCuadro, texto: r.plural, fondo: r.fondo })),
  { clave: 'LIMITADAS', texto: 'Las limitó una regla', fondo: 'bg-baja' },
];

export function ProductosDeLaCorridaDialog({
  evento,
  estrategiaDeLaParte,
  nombreDeLaParte,
  onCerrar,
  onIrA,
}: {
  /** null = cerrado. */
  evento: EventoDeLaLinea | null;
  /** Un recálculo visto desde una estrategia: sólo su parte. */
  estrategiaDeLaParte: string | null;
  nombreDeLaParte?: string | null;
  onCerrar: () => void;
  onIrA?: (
    destino: DestinoHoy,
    strategyApplicationId: string | null,
    filtro?: FiltroBandeja
  ) => void;
}) {
  const [datos, setDatos] = useState<ProductosDelEvento | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [vuelta, setVuelta] = useState(0);
  const [filtros, setFiltros] = useState<FiltrosDelCuadro>(FILTROS_DEL_CUADRO);
  const [orden, setOrden] = useState<OrdenDelCuadro>(ORDEN_INICIAL);
  const [historialDe, setHistorialDe] = useState<{ id: string; nombre: string } | null>(null);
  const { toast } = useToast();
  const [bajando, setBajando] = useState(false);
  const cambiar = (c: Partial<FiltrosDelCuadro>) => setFiltros((f) => ({ ...f, ...c }));

  // Ronda de arreglos (revisión T12): el cuadro depende de QUÉ evento es (id y
  // tipo), no del objeto: un refresco de la línea trae otro objeto del mismo
  // evento y no tiene que reiniciar los filtros ni volver a pedir.
  const eventoActual = useRef(evento);
  eventoActual.current = evento;
  const eventoId = evento?.id;
  const eventoTipo = evento?.tipo;
  useEffect(() => {
    const e = eventoActual.current;
    if (!e) return;
    let vigente = true;
    setDatos(null);
    setError(null);
    setFiltros(FILTROS_DEL_CUADRO);
    setOrden(ORDEN_INICIAL);
    corridasApi
      .productos(e, estrategiaDeLaParte)
      .then((r) => vigente && setDatos(r))
      .catch((e) => vigente && setError(mensajeDeError(e, 'No se pudieron traer los productos.')));
    return () => {
      vigente = false;
    };
  }, [eventoId, eventoTipo, estrategiaDeLaParte, vuelta]);

  const filas = datos?.filas ?? [];
  const esRecalculo = evento?.tipo === 'RECALCULO';
  const deTodas = esRecalculo && !estrategiaDeLaParte;
  const chips = useMemo(() => contarChips(filas, filtros), [filas, filtros]);
  const visibles = useMemo(
    () =>
      ordenar(
        filas.filter((f) => pasaElFiltro(f, filtros)),
        orden
      ),
    [filas, filtros, orden]
  );
  const { suma, promedio } = totales(visibles);

  async function bajarExcel() {
    if (!evento) return;
    setBajando(true);
    try {
      await corridasApi.excel(
        evento,
        estrategiaDeLaParte,
        visibles.map((f) => f.id)
      );
    } catch (err) {
      toast({
        title: 'No se pudo descargar el Excel',
        description: mensajeDeError(err, 'Probá de nuevo.'),
        variant: 'destructive',
      });
    } finally {
      setBajando(false);
    }
  }

  if (!evento) return null;
  const e = datos?.evento ?? evento;
  const titulo = esRecalculo
    ? `Productos del recálculo de reglas · ${estrategiaDeLaParte ? `parte de «${nombreDeLaParte ?? '—'}»` : 'todas las estrategias'}`
    : `Productos de la corrida · ${e.estrategia?.nombre ?? '—'}`;
  const subtitulo = `${tituloDelDia(diaDelEvento(e), diaDeBolivia())}, ${formatoFechaYHora(e.cuando)} (hora de Bolivia) · ${
    esRecalculo ? 'Recálculo de reglas' : cortoDelDisparo(e.disparo)
  } · ${quienLaLanzo(e)} · ${esRecalculo ? 'recalculó' : 'revisó'} ${productosOGuion(e.revisados)}, propuso ${cantidadOGuion(e.conteos ? cuantasPropuso(e) : null)}${esRecalculo ? ' · todo va a Por decidir' : ''}`;

  const selector = (
    etiqueta: string,
    valor: string,
    opciones: string[],
    todas: string,
    onCambiar: (v: string) => void
  ) => (
    <label className="flex flex-col gap-1 text-xs text-muted-foreground">
      {etiqueta}
      <SelectNativo
        aria-label={etiqueta}
        className="h-9 w-full"
        value={valor}
        onChange={(ev) => onCambiar(ev.target.value)}
      >
        <option value="">{todas}</option>
        {opciones.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </SelectNativo>
    </label>
  );

  return (
    <Dialog open onOpenChange={(abierto) => !abierto && onCerrar()}>
      <DialogContent
        data-testid="productos-de-la-corrida"
        className="flex h-[92vh] w-[96vw] max-w-[1600px] flex-col gap-0 overflow-hidden p-0"
      >
        <div className="border-b px-5 pb-3 pr-12 pt-4">
          <DialogTitle className={cn('text-base', TEXTO)}>{titulo}</DialogTitle>
          <DialogDescription className={cn('mt-1 text-sm', TEXTO)}>{subtitulo}</DialogDescription>
          <div
            className="mt-3 flex flex-wrap items-center gap-1.5"
            role="group"
            aria-label="Qué pasó"
          >
            {CHIPS.filter((c) => !(esRecalculo && c.clave === 'SOLA')).map((c) => (
              <button
                key={c.clave}
                type="button"
                aria-pressed={filtros.chip === c.clave}
                onClick={() => cambiar({ chip: c.clave })}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs',
                  filtros.chip === c.clave
                    ? 'border-primary bg-primary/10 font-semibold text-primary'
                    : 'text-muted-foreground'
                )}
              >
                {c.fondo && (
                  <span aria-hidden="true" className={cn('h-2 w-2 rounded-full', c.fondo)} />
                )}
                {c.texto} <b className="tabular-nums">{formatoCantidad(chips[c.clave])}</b>
              </button>
            ))}
            {esRecalculo && (
              <span className="text-xs text-muted-foreground">
                Un recálculo de reglas nunca aplica nada solo: todo va a Por decidir.
              </span>
            )}
          </div>
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-[16rem_minmax(0,1fr)]">
          <aside
            aria-label="Filtros"
            className="space-y-4 overflow-y-auto border-r bg-muted/30 p-4"
          >
            <label className="flex flex-col gap-1 text-xs text-muted-foreground">
              Buscar
              <span className="relative">
                <Search aria-hidden="true" className="absolute left-2.5 top-2.5 h-4 w-4" />
                <Input
                  type="search"
                  className="pl-8"
                  placeholder="Nombre o código"
                  aria-label="Buscar un producto"
                  value={filtros.q}
                  onChange={(ev) => cambiar({ q: ev.target.value })}
                />
              </span>
            </label>
            {deTodas &&
              selector(
                'Estrategia',
                filtros.estrategia,
                opcionesDe(filas, (f) => f.estrategia),
                'Todas',
                (v) => cambiar({ estrategia: v })
              )}
            {selector(
              'Categoría',
              filtros.categoria,
              opcionesDe(filas, categoriaDe),
              'Todas',
              (v) => cambiar({ categoria: v })
            )}
            {selector('Marca', filtros.marca, opcionesDe(filas, marcaDe), 'Todas', (v) =>
              cambiar({ marca: v })
            )}
            {selector(
              'Competidor más barato',
              filtros.competidor,
              opcionesDe(filas, competidorDe),
              'Todos',
              (v) => cambiar({ competidor: v })
            )}
            <div className="flex flex-col gap-1 text-xs text-muted-foreground">
              Dirección
              <GrupoDeOpciones
                aria-label="Dirección"
                valor={filtros.direccion}
                onCambiar={(direccion) => cambiar({ direccion })}
                opciones={[
                  { valor: 'TODOS', texto: 'Todos' },
                  { valor: 'BAJAN', texto: 'Bajan' },
                  { valor: 'SUBEN', texto: 'Suben' },
                ]}
              />
            </div>
            {(
              [
                {
                  k: 'cambio',
                  titulo: 'Cambio propuesto',
                  corto: 'Cambio',
                  rango: CAMBIO,
                  signo: true,
                },
                {
                  k: 'margen',
                  titulo: 'Margen resultante',
                  corto: 'Margen',
                  rango: MARGEN,
                  signo: false,
                },
              ] as const
            ).map((b) => (
              <div key={b.k} className="space-y-1.5 text-xs text-muted-foreground">
                <span>{b.titulo}</span>
                <div className="flex justify-between tabular-nums text-foreground">
                  <span>{textoDelExtremo(filtros[b.k][0], 'desde', b.rango, b.signo)}</span>
                  <span>{textoDelExtremo(filtros[b.k][1], 'hasta', b.rango, b.signo)}</span>
                </div>
                <Slider
                  aria-label={b.titulo}
                  etiquetasDeLosTiradores={[`${b.corto}, desde`, `${b.corto}, hasta`]}
                  min={b.rango.min}
                  max={b.rango.max}
                  step={1}
                  value={filtros[b.k]}
                  onValueChange={(v) =>
                    cambiar({ [b.k]: [v[0], v[1]] } as Partial<FiltrosDelCuadro>)
                  }
                />
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={() => setFiltros(FILTROS_DEL_CUADRO)}>
              Limpiar filtros
            </Button>
            <p className="text-xs text-muted-foreground">
              Los extremos de cada barra no ponen tope: en −25 % entran también los que bajan más.
            </p>
          </aside>

          <div className="flex min-h-0 min-w-0 flex-col">
            <div className="flex flex-wrap items-center gap-3 border-b px-4 py-2 text-sm">
              {/* Ronda de arreglos: sin datos (cargando o con error) no hay «0 productos». */}
              {datos && !error && (
                <span>
                  <b className="tabular-nums">{formatoCantidad(visibles.length)}</b>{' '}
                  {visibles.length === 1 ? 'producto' : 'productos'}
                </span>
              )}
              <span className="ml-auto text-xs text-muted-foreground">
                Tocá el título de una columna para ordenar
              </span>
            </div>
            {datos?.recortadas && (
              <Aviso tono="info" className="m-3">
                Se muestran los primeros 5.000 productos de esta corrida.
              </Aviso>
            )}
            <div
              className="min-h-0 flex-1 overflow-y-auto"
              data-testid="tabla-de-productos-de-la-corrida"
            >
              {error ? (
                <div className="p-4">
                  <EstadoError onReintentar={() => setVuelta((x) => x + 1)}>{error}</EstadoError>
                </div>
              ) : !datos ? (
                <div className="space-y-2 p-4">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <Skeleton key={i} className="h-10 w-full" />
                  ))}
                </div>
              ) : (
                <table className="w-full table-fixed text-sm">
                  <colgroup>
                    {COLUMNAS.map((c) => (
                      <col key={c.titulo} style={{ width: `${c.ancho}%` }} />
                    ))}
                  </colgroup>
                  <thead className="sticky top-0 z-10 bg-card shadow-card">
                    <tr>
                      {COLUMNAS.map((c) => {
                        const activa = c.columna != null && orden.columna === c.columna;
                        return (
                          <th
                            key={c.titulo}
                            scope="col"
                            aria-sort={
                              activa ? (orden.asc ? 'ascending' : 'descending') : undefined
                            }
                            className={cn(
                              'px-2 py-2 text-xs font-semibold text-muted-foreground',
                              c.derecha ? 'text-right' : 'text-left'
                            )}
                          >
                            {c.columna ? (
                              <button
                                type="button"
                                onClick={() => setOrden((o) => cambiarOrden(o, c.columna!))}
                                className={cn(
                                  'inline-flex max-w-full items-center gap-1 text-left [overflow-wrap:anywhere] hover:text-foreground',
                                  activa && 'text-foreground'
                                )}
                              >
                                <span className="min-w-0">{c.titulo}</span>
                                {activa ? (
                                  orden.asc ? (
                                    <ArrowUp className="h-3 w-3 shrink-0 text-primary" />
                                  ) : (
                                    <ArrowDown className="h-3 w-3 shrink-0 text-primary" />
                                  )
                                ) : (
                                  <ArrowUpDown className="h-3 w-3 shrink-0 opacity-40" />
                                )}
                              </button>
                            ) : (
                              c.titulo
                            )}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {visibles.length === 0 ? (
                      <tr>
                        <td
                          colSpan={COLUMNAS.length}
                          className="p-6 text-center text-muted-foreground"
                        >
                          {/* Ronda del QA de R4 (R4-35): una corrida a la que otra
                              corrida o un recálculo le reescribió todo no tiene
                              filas a su nombre: se dice, no «con estos filtros». */}
                          {filas.length === 0 && !esRecalculo
                            ? 'Ya no queda ninguna a su nombre: lo que propuso lo reescribió después otra corrida o un recálculo.'
                            : 'Ningún producto con estos filtros.'}
                        </td>
                      </tr>
                    ) : (
                      visibles.map((f) => {
                        const r = RESULTADOS.find((x) => x.clave === f.resultado)!;
                        // El porqué ya trae el motivo del descarte con su texto (nunca la clave).
                        const porQue = porQueDelResultado(f, e);
                        return (
                          <tr
                            key={f.id}
                            className="border-b align-top hover:bg-muted/30"
                            data-testid="fila-de-la-corrida"
                          >
                            <td className={cn('px-2 py-2', TEXTO)}>
                              <div className={TEXTO}>{f.nombre}</div>
                              <div className={cn('text-xs text-muted-foreground', TEXTO)}>
                                {f.sku} · {categoriaDe(f)} · {marcaDe(f)}
                                {deTodas && f.estrategia ? ` · «${f.estrategia}»` : ''}
                              </div>
                            </td>
                            <td className="px-2 py-2 text-right tabular-nums">
                              <div className="text-xs text-muted-foreground">
                                {formatoPrecio(f.antes)}
                              </div>
                              <div className="font-semibold">→ {formatoPrecio(f.propuesto)}</div>
                            </td>
                            <td className="px-2 py-2 text-right tabular-nums">
                              <div
                                className={
                                  f.cambio > 0.005
                                    ? 'text-ok'
                                    : f.cambio < -0.005
                                      ? 'text-baja'
                                      : 'text-muted-foreground'
                                }
                              >
                                {f.cambioPct == null
                                  ? '—'
                                  : formatoPorcentaje(f.cambioPct, { signo: true })}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {formatoDiferencia(f.cambio)}
                              </div>
                            </td>
                            <td className="px-2 py-2 text-right tabular-nums">
                              {f.margenPct == null ? '—' : formatoPorcentaje(f.margenPct)}
                            </td>
                            <td className={cn('px-2 py-2 text-right tabular-nums', TEXTO)}>
                              {f.masBarato ? (
                                <>
                                  <div>
                                    <EnlaceAProducto
                                      href={f.masBarato.url}
                                      tienda={f.masBarato.tienda}
                                    >
                                      {formatoPrecio(f.masBarato.precio)}
                                    </EnlaceAProducto>
                                  </div>
                                  <div className={cn('text-xs text-muted-foreground', TEXTO)}>
                                    {f.masBarato.tienda ?? '—'}
                                  </div>
                                </>
                              ) : (
                                '—'
                              )}
                            </td>
                            <td className={cn('space-y-1 px-2 py-2', TEXTO)}>
                              <Etiqueta tono={r.tono} className="whitespace-normal">
                                {r.singular}
                              </Etiqueta>
                              {porQue && (
                                <div className={cn('text-xs text-muted-foreground', TEXTO)}>
                                  {porQue}
                                </div>
                              )}
                              {f.reescritaDespues && (
                                <div className={cn('text-xs text-muted-foreground', TEXTO)}>
                                  La reescribió después otra corrida o un recálculo
                                </div>
                              )}
                              {f.resultado === 'DESCARTADA' && f.nota && (
                                <TextoRecortable texto={`«${f.nota}»`} className="text-xs" />
                              )}
                              {f.limitada && (
                                <div className="space-y-0.5">
                                  <Etiqueta
                                    tono="alerta"
                                    className="whitespace-normal [overflow-wrap:anywhere]"
                                  >
                                    La limitó «{f.limitada.regla ?? 'una regla'}»
                                  </Etiqueta>
                                  {f.limitada.queria != null && (
                                    <div className={cn('text-xs text-muted-foreground', TEXTO)}>
                                      quería {formatoPrecio(f.limitada.queria)}, quedó en{' '}
                                      {formatoPrecio(f.propuesto)}
                                    </div>
                                  )}
                                </div>
                              )}
                            </td>
                            <td className="px-2 py-2 text-right tabular-nums">
                              <div>{formatoPrecio(f.precioDeHoy)}</div>
                              {sinCambiosHoy(f) && (
                                <div className="text-xs text-muted-foreground">sin cambios</div>
                              )}
                            </td>
                            <td className="px-2 py-2">
                              <div className="flex min-w-0 flex-col items-end gap-1">
                                {f.resultado === 'ESPERA' && onIrA && (
                                  <Button
                                    size="sm"
                                    className="h-auto whitespace-normal px-2 py-1 text-xs leading-tight"
                                    onClick={() => {
                                      onCerrar();
                                      onIrA('BANDEJA', f.estrategiaId, { productId: f.productId });
                                    }}
                                  >
                                    Abrir en Por decidir
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="link"
                                  className="h-auto whitespace-normal p-0 text-xs"
                                  onClick={() =>
                                    setHistorialDe({ id: f.productId, nombre: f.nombre })
                                  }
                                >
                                  Historial
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        <div
          className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t px-5 py-3 text-sm"
          data-testid="pie-del-cuadro"
        >
          {datos && !error && (
            <span className="text-muted-foreground">
              Mostrando {formatoCantidad(visibles.length)} de {formatoCantidad(filas.length)}
            </span>
          )}
          {datos && !error && visibles.length > 0 && (
            <span>
              Suma del cambio:{' '}
              <b className={cn('tabular-nums', suma < 0 ? 'text-baja' : 'text-ok')}>
                {formatoDiferencia(suma)} Bs
              </b>
              {' · '}Cambio promedio:{' '}
              <b className={cn('tabular-nums', (promedio ?? 0) < 0 ? 'text-baja' : 'text-ok')}>
                {promedio == null ? '—' : formatoPorcentaje(promedio, { signo: true })}
              </b>
            </span>
          )}
          <span className="text-xs text-muted-foreground">Hora de Bolivia</span>
          <Button
            variant="outline"
            className="ml-auto"
            disabled={visibles.length === 0 || bajando}
            onClick={() => void bajarExcel()}
          >
            Descargar Excel
          </Button>
          <Button variant="outline" onClick={onCerrar}>
            Cerrar
          </Button>
        </div>

        <ProductHistoryDialog
          open={historialDe != null}
          onOpenChange={(abierto) => !abierto && setHistorialDe(null)}
          productId={historialDe?.id ?? null}
          productName={historialDe?.nombre ?? ''}
        />
      </DialogContent>
    </Dialog>
  );
}
